import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

import { useLanguage } from '../../../contexts/LanguageContext';

const ExecutiveCard = ({ executive }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useLanguage();

  return (
    <div className="bg-white/40 backdrop-blur-xl border border-white/20 rounded-3xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-[1.02]">
      {/* Header */}
      <div className="flex items-start gap-4 mb-5">
        <div className="w-16 h-16 rounded-full bg-[#0025cc] flex items-center justify-center flex-shrink-0">
          <Icon name="User" size={32} color="#FFFFFF" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-2">
            <h3 className="text-xl font-bold text-[#111827]">{executive?.title}</h3>
            <div className="flex items-center gap-1 px-3 py-1.5 bg-green-100 rounded-xl flex-shrink-0 shadow-sm">
              <Icon name="TrendingUp" size={16} color="#10B981" />
              <span className="text-sm font-bold text-green-700">{executive?.matchScore}%</span>
            </div>
          </div>
          <div className="flex items-center gap-2 text-[#0025cc] mb-1">
            <Icon name="Award" size={16} color="#0025cc" />
            <span className="text-sm font-semibold">{executive?.experience} {t('preview.experience')}</span>
          </div>
          {executive?.companyScale && (
            <div className="flex items-center gap-2 text-[#6B7280]">
              <Icon name="Building2" size={14} color="#6B7280" />
              <span className="text-xs font-medium">{executive?.companyScale}</span>
            </div>
          )}
        </div>
      </div>
      {/* Industries */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <Icon name="Building2" size={14} color="#6B7280" />
          <span className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide">{t('preview.industries')}</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {executive?.industries?.map((industry, index) => (
            <span 
              key={index}
              className="px-3 py-1 bg-[#0025cc]/10 text-[#0025cc] text-sm font-medium rounded-lg border border-[#0025cc]/20"
            >
              {industry}
            </span>
          ))}
        </div>
      </div>
      {/* Candidate Description */}
      {executive?.description && (
        <div className="mb-4 p-4 bg-gradient-to-br from-blue-50/50 to-indigo-50/30 rounded-xl border border-blue-100/50">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="FileText" size={14} color="#0025cc" />
            <span className="text-xs font-semibold text-[#0025cc] uppercase tracking-wide">{t('preview.professionalProfile')}</span>
          </div>
          <p className="text-sm text-[#374151] leading-relaxed">
            {executive?.description}
          </p>
        </div>
      )}
      {/* Skills */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <Icon name="Zap" size={14} color="#6B7280" />
          <span className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide">{t('preview.keySkills')}</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {executive?.skills?.slice(0, isExpanded ? executive?.skills?.length : 4)?.map((skill, index) => (
            <span 
              key={index}
              className="px-2.5 py-1 bg-white/60 backdrop-blur-sm text-[#111827] text-xs font-medium rounded-lg border border-[#E5E7EB] shadow-sm"
            >
              {skill}
            </span>
          ))}
          {!isExpanded && executive?.skills?.length > 4 && (
            <button
              onClick={() => setIsExpanded(true)}
              className="px-2.5 py-1 bg-[#0025cc]/10 text-[#0025cc] text-xs font-medium rounded-lg border border-[#0025cc]/20 hover:bg-[#0025cc]/20 transition-colors"
            >
              +{executive?.skills?.length - 4} {t('preview.more')}
            </button>
          )}
        </div>
      </div>
      {/* Achievements (Expandable) */}
      {isExpanded && (
        <div className="mb-4 animate-in fade-in duration-300">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Award" size={14} color="#6B7280" />
            <span className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide">{t('preview.keyAchievements')}</span>
          </div>
          <ul className="space-y-2">
            {executive?.achievements?.map((achievement, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-[#374151]">
                <Icon name="CheckCircle2" size={16} color="#10B981" className="flex-shrink-0 mt-0.5" />
                <span>{achievement}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {/* Footer */}
      <div className="flex items-center justify-between pt-4 border-t border-white/30">
        <div className="flex items-center gap-2">
          {executive?.availability === 'Open to opportunities' ? (
            <div className="relative px-5 py-2.5 rounded-2xl overflow-hidden group">
              {/* Liquid glass effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-green-400/30 via-emerald-400/20 to-teal-400/30 backdrop-blur-xl"></div>
              <div className="absolute inset-0 bg-white/10 backdrop-blur-sm"></div>
              <div className="absolute inset-[1px] rounded-2xl bg-gradient-to-br from-white/40 to-white/10 backdrop-blur-md"></div>
              {/* Shine effect */}
              <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
              {/* Content */}
              <span className="relative text-sm font-bold bg-gradient-to-r from-green-700 to-emerald-700 bg-clip-text text-transparent">{t('preview.availableOnMarket')}</span>
            </div>
          ) : (
            <div className="relative px-5 py-2.5 rounded-2xl overflow-hidden group">
              {/* Liquid glass effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-amber-400/30 via-orange-400/20 to-yellow-400/30 backdrop-blur-xl"></div>
              <div className="absolute inset-0 bg-white/10 backdrop-blur-sm"></div>
              <div className="absolute inset-[1px] rounded-2xl bg-gradient-to-br from-white/40 to-white/10 backdrop-blur-md"></div>
              {/* Shine effect */}
              <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
              {/* Content */}
              <span className="relative text-sm font-bold bg-gradient-to-r from-amber-700 to-orange-700 bg-clip-text text-transparent">{t('preview.openToOpportunities')}</span>
            </div>
          )}
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#0025cc] hover:bg-[#0025cc]/10 rounded-lg transition-colors"
        >
          <span>{isExpanded ? t('preview.showLess') : t('preview.viewDetails')}</span>
          <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={16} color="#0025cc" />
        </button>
      </div>
    </div>
  );
};

export default ExecutiveCard;